import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Sparkles } from 'lucide-react';
import { DiffViewer } from './DiffViewer';
import { useRecordDocumentationChangeMutation } from '@/store/documentationApi';
import { toast } from 'sonner';

interface ChangeSummaryDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    docId: number;
    diff: string;
    editorName?: string;
}

/**
 * דיאלוג המוצג לאחר שמירת מסמך קיים.
 * מציג את ה-diff ומאפשר למשתמש לרשום תיאור לשינוי.
 * בשלב הבא: תיאור יוזרק אוטומטית על ידי Gemini.
 */
export function ChangeSummaryDialog({
    open,
    onOpenChange,
    docId,
    diff,
    editorName = '',
}: ChangeSummaryDialogProps) {
    const [description, setDescription] = useState('');
    const [recordChange, { isLoading }] = useRecordDocumentationChangeMutation();

    async function handleSubmit() {
        try {
            await recordChange({ id: docId, diff, description, editor_name: editorName }).unwrap();
            toast.success('השינוי תועד בהצלחה');
            setDescription('');
            onOpenChange(false);
        } catch {
            toast.error('שגיאה בתיעוד השינוי');
        }
    }

    function handleSkip() {
        setDescription('');
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl w-full max-h-[90vh] flex flex-col gap-0 p-0"
                dir="rtl"
            >
                <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
                    <DialogTitle className="text-base font-semibold">תיעוד השינוי</DialogTitle>
                    <p className="text-sm text-muted-foreground">המסמך נשמר בהצלחה. אפשר לתעד את מהות השינוי לצורך מעקב</p>
                </DialogHeader>

                <div className="flex flex-col gap-4 px-6 py-4 overflow-y-auto flex-1">
                    {/* diff */}
                    <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                            שינויים שבוצעו
                        </p>
                        <DiffViewer diff={diff} className="max-h-64" />
                    </div>

                    {/* description */}
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            תיאור השינוי
                            <span className="text-muted-foreground font-normal mr-1">(אופציונלי)</span>
                        </label>
                        <Textarea
                            dir="rtl"
                            placeholder="מה השתנה? מדוע? הערות נוספות…"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            בקרוב: תיאור יוצר אוטומטית על ידי Gemini AI
                        </p>
                    </div>
                </div>

                <DialogFooter className="px-6 py-4 border-t shrink-0 flex gap-2 justify-start flex-row-reverse">
                    <Button onClick={handleSubmit} disabled={isLoading} className="gap-1">
                        {isLoading && <Spinner className="h-4 w-4" />}
                        שמור תיעוד
                    </Button>
                    <Button variant="ghost" onClick={handleSkip} disabled={isLoading}>
                        דלג
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
